import React from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import DefaultLayout from "./src/layout/default-layout";
import OverviewPage from "./src/pages/overview-page";
import LoginLayout from "./src/layout/login-layout";
import { ToastContainer } from "react-toastify";
import Login from "./src/pages/login";
import AppProvider from "./src/providers/app-context.jsx";
import AuthProvider from "./src/providers/auth-provider.jsx";
import PerformancePage from "./src/pages/performance-page.jsx";
import ManageOrganisationsPage from "./src/pages/manage-organisations-page.jsx";
import OrganisationPage from "./src/pages/organisation-page.jsx";
import ManageChannelsPage from "./src/pages/manage-channels-page.jsx";
import ChannelPage from "./src/pages/channel-page.jsx";
import MonetisationPage from "./src/pages/monetisation-page.jsx";
import AvodPage from "./src/pages/avod-page.jsx";
import OneLinkBridgePage from "./src/pages/one-link-bridge-page.jsx";
import ManageAccountPage from "./src/pages/manage-account-page";
import MaintenancePage from "./src/pages/maintenance-page.jsx";
import ReportsPage from "./src/pages/reports-page.jsx";
import RequirePermission from "./src/components/require-permission.jsx";
import { PERMISSIONS } from "./src/functions/has-permission.js";

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <LoginLayout title="GoSeeTV Insights" sidebarId="login">
        <Login />
      </LoginLayout>
    ),
  },
  {
    path: "/",
    element: (
      <RequirePermission permission={PERMISSIONS.HBBTV_VIEWERSHIP} fallback="/account">
        <DefaultLayout title="GSTV Insights - Overview" sidebarId="overview">
          <OverviewPage />
        </DefaultLayout>
      </RequirePermission>
    ),
  },
  {
    path: "/performance",
    element: (
      <RequirePermission permission={PERMISSIONS.HBBTV_VIEWERSHIP}>
        <DefaultLayout
          title="GSTV Insights - Performance"
          sidebarId="performance"
        >
          <PerformancePage />
        </DefaultLayout>
      </RequirePermission>
    ),
  },
  {
    path: "/monetisation",
    element: (
      <RequirePermission permission={PERMISSIONS.HBBTV_VIEWERSHIP}>
        <DefaultLayout
          title="GSTV Insights - Monetisation"
          sidebarId="monetisation"
        >
          <MonetisationPage />
        </DefaultLayout>
      </RequirePermission>
    ),
  },
  {
    path: "/avod",
    element: (
      <RequirePermission permission={PERMISSIONS.HBBTV_VIEWERSHIP}>
        <DefaultLayout title="GSTV Insights - AVOD" sidebarId="avod">
          <AvodPage />
        </DefaultLayout>
      </RequirePermission>
    ),
  },
  {
    path: "/one-link-bridge",
    element: (
      <RequirePermission permission={PERMISSIONS.ONE_LINK}>
        <DefaultLayout title="GSTV Insights - One Link Bridge" sidebarId="one-link-bridge">
          <OneLinkBridgePage />
        </DefaultLayout>
      </RequirePermission>
    ),
  },
  {
    path: "/reports",
    element: (
      <DefaultLayout title="GSTV Insights - Reports" sidebarId="reports">
        <ReportsPage />
      </DefaultLayout>
    ),
  },
  {
    path: "/account",
    element: (
        <DefaultLayout title="GSTV Insights - Account" sidebarId="manage-account">
          <ManageAccountPage />
        </DefaultLayout>
    ),
  },
  {
    path: "/admin",
    element: <Outlet />,
    children: [
      {
        path: "organisations",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <DefaultLayout
                title="GSTV Insights Admin - Organisations"
                sidebarId="admin-organisations"
              >
                <ManageOrganisationsPage />
              </DefaultLayout>
            ),
          },
          {
            path: ":organisation_id",
            element: (
              <DefaultLayout
                title="GSTV Insights Admin - Organisation"
                sidebarId="admin-organisations"
              >
                <OrganisationPage />
              </DefaultLayout>
            ),
          },
        ],
      },
      {
        path: "channels",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <DefaultLayout
                title="GSTV Insights Admin - Channels"
                sidebarId="admin-channels"
              >
                <ManageChannelsPage />
              </DefaultLayout>
            ),
          },
          {
            path: ":channel_id",
            element: (
              <DefaultLayout
                title="GSTV Insights Admin - Channel"
                sidebarId="admin-channels"
              >
                <ChannelPage />
              </DefaultLayout>
            ),
          },
        ],
      },
    ],
  },
]);

const MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE === "true";

function App() {
  if (MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }

  return (
    <AuthProvider>
      <AppProvider>
        <ToastContainer position="top-center" autoClose={3000} theme="dark" />
        <RouterProvider router={router}></RouterProvider>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
